import {
  Box,
  Typography,
  TableContainer,
  TableCell,
  Table,
  Paper,
  TableHead,
  TableRow,
  TableBody,
  IconButton,
  Button,
  Skeleton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import DownloadIcon from '@mui/icons-material/Download';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { useNavigate, useParams } from "react-router-dom";
import { FunctionComponent, useState, useMemo, useEffect } from "react";
import DashboardLayout from "../../components/layout/layout";
import { useGetCompanyOpenItems } from "../../hooks/api/oms";
import moment from "moment";

const TableCol = styled(TableCell)(({ theme }) => ({
  border: '1px solid rgba(224, 224, 224, 1)',
}));

type SortField = 'orderNumber' | 'purchaseOrder' | 'sortName' | 'lineNumber' | 'itemNumber' | 'customerItem' | 'quantityOpen' | 'estimatedShipDate';
type SortDirection = 'asc' | 'desc' | null;
type SortState = { field: SortField | null; direction: SortDirection };

export interface OpenItemsData {
  content: Content[];
}

export interface Content {
  orderNumber: string;
  purchaseOrder: string;
  sortName: string;
  lineNumber: string;
  itemNumber:string;
  customerItem:string;
  quantityOrdered: string,
  quantityShipped: string,
  quantityOpen: string,
  quantityOpenDisplay: string; // For "Item ready to ship" display
  estimatedShipDate: string,
  promiseDate: string,
  itemType: string
}

const CheckOrderStatusItemDetails: FunctionComponent = () => {
  const navigate = useNavigate();
  const { orderNumber } = useParams();

  const {data: openItem, isFetched: hasFetched, isLoading} = useGetCompanyOpenItems(orderNumber);

  // Sorting state
  const [sortState, setSortState] = useState<SortState>({ field: null, direction: null });

  // Download state
  const [downloadAnchorEl, setDownloadAnchorEl] = useState<null | HTMLElement>(null);
  const downloadMenuOpen = Boolean(downloadAnchorEl);

  const handleDownloadClick = (event: React.MouseEvent<HTMLElement>) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  const downloadFormats = [
    { label: 'CSV (.csv)', format: 'csv', icon: '📊' },
    //{ label: 'Excel (.xlsx)', format: 'xlsx', icon: '📈' },
   // { label: 'PDF (.pdf)', format: 'pdf', icon: '📄' },
    { label: 'Word (.docx)', format: 'docx', icon: '📝' },
    { label: 'Text (.txt)', format: 'txt', icon: '📃' },
  ];

  const handleDownload = (format: string) => {
    handleDownloadClose();
    
    // Get the full dataset (not just current page)
    const dataToExport = sortedOpenItems;
    
    if (format === 'csv') {
      downloadAsCSV(dataToExport);
    } 
    // else if (format === 'xlsx') {
    //   downloadAsExcel(dataToExport);
    // } 
    // else if (format === 'pdf') {
    //   downloadAsPDF(dataToExport);
    // } 
    else if (format === 'docx') {
      downloadAsWord(dataToExport);
    } else if (format === 'txt') {
      downloadAsText(dataToExport);
    }
  };

  const downloadAsCSV = (data: any[]) => {
    const headers = [
      'Sales Order',
      'Purchase Order', 
      'Sort Name',
      'Line',
      'Item Number',
      'Customer Item',
      'Open Qty',
      'Estimated Ship Date'
    ];
    
    const csvContent = [
      headers.join(','),
      ...data.map(item => [
        `"${item.orderNumber || ''}"`,
        `"${item.purchaseOrder || ''}"`,
        `"${item.sortName || ''}"`,
        `"${item.lineNumber || ''}"`,
        `"${item.itemNumber || ''}"`,
        `"${item.customerItem || ''}"`,
        `"${item.quantityOpenDisplay || item.quantityOpen || ''}"`,
        `"${formatEstimatedShipDate(item.estimatedShipDate) || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `open_items_${orderNumber}_${moment().format('YYYY-MM-DD')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // const downloadAsExcel = (data: any[]) => {
  //   // Create Excel-compatible CSV with proper encoding
  //   const headers = [
  //     'Sales Order',
  //     'Purchase Order', 
  //     'Sort Name',
  //     'Line',
  //     'Item Number',
  //     'Customer Item',
  //     'Open Qty',
  //     'Estimated Ship Date'
  //   ];
    
  //   const csvContent = [
  //     headers.join(','),
  //     ...data.map(item => [
  //       `"${item.orderNumber || ''}"`,
  //       `"${item.purchaseOrder || ''}"`,
  //       `"${item.sortName || ''}"`,
  //       `"${item.lineNumber || ''}"`,
  //       `"${item.itemNumber || ''}"`,
  //       `"${item.customerItem || ''}"`,
  //       `"${item.quantityOpen || ''}"`,
  //       `"${formatEstimatedShipDate(item.estimatedShipDate) || ''}"`
  //     ].join(','))
  //   ].join('\n');

  //   // Add BOM for Excel compatibility
  //   const BOM = '\uFEFF';
  //   const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  //   const link = document.createElement('a');
  //   const url = URL.createObjectURL(blob);
  //   link.setAttribute('href', url);
  //   link.setAttribute('download', `open_items_${orderNumber}_${moment().format('YYYY-MM-DD')}.xlsx`);
  //   link.style.visibility = 'hidden';
  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);
  // };

  // const downloadAsPDF = (data: any[]) => {
  //   // Create a formatted text file that can be easily converted to PDF
  //   const pdfContent = [
  //     'OPEN ITEMS REPORT',
  //     '='.repeat(50),
  //     '',
  //     `Generated on: ${moment().format('MM/DD/YYYY HH:mm:ss')}`,
  //     `Customer: ${orderNumber}`,
  //     '',
  //     'SALES ORDER | PURCHASE ORDER | SORT NAME | LINE | ITEM NUMBER | CUSTOMER ITEM | OPEN QTY | ESTIMATED SHIP DATE',
  //     '-'.repeat(120),
  //     ...data.map(item => [
  //       padEnd(item.orderNumber || '', 12),
  //       padEnd(item.purchaseOrder || '', 15),
  //       padEnd(item.sortName || '', 12),
  //       padEnd(item.lineNumber || '', 6),
  //       padEnd(item.itemNumber || '', 15),
  //       padEnd(item.customerItem || '', 15),
  //       padEnd(item.quantityOpen || '', 8),
  //       formatEstimatedShipDate(item.estimatedShipDate) || ''
  //     ].join(' | '))
  //   ].join('\n');

  //   // Create a text file that can be opened and printed to PDF
  //   const blob = new Blob([pdfContent], { type: 'text/plain;charset=utf-8;' });
  //   const link = document.createElement('a');
  //   const url = URL.createObjectURL(blob);
  //   link.setAttribute('href', url);
  //   link.setAttribute('download', `open_items_${orderNumber}_${moment().format('YYYY-MM-DD')}.pdf`);
  //   link.style.visibility = 'hidden';
  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);
  // };

  const downloadAsWord = (data: any[]) => {
    // Create Word-compatible HTML content that can be opened in Word
    const wordContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Open Items Report</title>
          <style>
            body { font-family: 'Times New Roman', serif; margin: 20px; }
            table { border-collapse: collapse; width: 100%; margin-top: 20px; }
            th, td { border: 1px solid #000; padding: 6px; text-align: left; }
            th { background-color: #f0f0f0; font-weight: bold; }
            h1 { color: #000; text-align: center; }
            .header { margin-bottom: 20px; text-align: center; }
            @page { margin: 1in; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Open Items Report</h1>
            <p><strong>Generated on:</strong> ${moment().format('MM/DD/YYYY HH:mm:ss')}</p>
            <p><strong>Customer:</strong> ${orderNumber}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Sales Order</th>
                <th>Purchase Order</th>
                <th>Sort Name</th>
                <th>Line</th>
                <th>Item Number</th>
                <th>Customer Item</th>
                <th>Open Qty</th>
                <th>Estimated Ship Date</th>
              </tr>
            </thead>
            <tbody>
              ${data.map(item => `
                <tr>
                  <td>${item.orderNumber || ''}</td>
                  <td>${item.purchaseOrder || ''}</td>
                  <td>${item.sortName || ''}</td>
                  <td>${item.lineNumber || ''}</td>
                  <td>${item.itemNumber || ''}</td>
                  <td>${item.customerItem || ''}</td>
                  <td>${item.quantityOpenDisplay || item.quantityOpen || ''}</td>
                  <td>${formatEstimatedShipDate(item.estimatedShipDate) || ''}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([wordContent], { type: 'application/msword;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `open_items_${orderNumber}_${moment().format('YYYY-MM-DD')}.doc`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAsText = (data: any[]) => {
    const textContent = [
      'Open Items Report',
      `Generated on: ${moment().format('MM/DD/YYYY HH:mm:ss')}`,
      `Customer: ${orderNumber}`,
      '',
      'Sales Order | Purchase Order | Sort Name | Line | Item Number | Customer Item | Open Qty | Estimated Ship Date',
      '--------------------------------------------------------------------------------------------------------',
      ...data.map(item => [
        item.orderNumber || '',
        item.purchaseOrder || '',
        item.sortName || '',
        item.lineNumber || '',
        item.itemNumber || '',
        item.customerItem || '',
        item.quantityOpenDisplay || item.quantityOpen || '',
        formatEstimatedShipDate(item.estimatedShipDate) || ''
      ].join(' | '))
    ].join('\n');

    const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `open_items_${orderNumber}_${moment().format('YYYY-MM-DD')}.txt`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Sorting logic - apply to full dataset first
  const sortedOpenItems = useMemo(() => {
    // Handle both array and object responses
    const list = Array.isArray(openItem) ? openItem : (openItem?.content || []);
    if (!sortState.field || !sortState.direction) return list;
    return [...list].sort((a, b) => {
      let aValue: any = a[sortState.field!];
      let bValue: any = b[sortState.field!];
      // Date field
      if (sortState.field === 'estimatedShipDate') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else if (sortState.field === 'quantityOpen' || sortState.field === 'lineNumber') {
        // For quantityOpen, use the numeric value for sorting, not the display text
        aValue = sortState.field === 'quantityOpen' ? (parseInt(a.quantityOpen) || 0) : (parseInt(aValue) || 0);
        bValue = sortState.field === 'quantityOpen' ? (parseInt(b.quantityOpen) || 0) : (parseInt(bValue) || 0);
      } else {
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
      }
      if (sortState.direction === 'asc') return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    });
  }, [openItem, sortState]);

  // Use all sorted data (no pagination)
  const displayData = useMemo(() => {
    return sortedOpenItems;
  }, [sortedOpenItems]);

  // Sort handler
  const handleSort = (field: SortField) => {
    setSortState(prev => {
      if (prev.field === field) {
        if (prev.direction === 'asc') return { field, direction: 'desc' };
        if (prev.direction === 'desc') return { field: null, direction: null };
      }
      return { field, direction: 'asc' };
    });

  };



  // Sort icon
  const getSortIcon = (field: SortField) => {
    if (sortState.field !== field) return <UnfoldMoreIcon fontSize="small" />;
    if (sortState.direction === 'asc') return <ArrowUpwardIcon fontSize="small" />;
    if (sortState.direction === 'desc') return <ArrowDownwardIcon fontSize="small" />;
    return <UnfoldMoreIcon fontSize="small" />;
  };

  const formatEstimatedShipDate = (dateStr: string): string => {
    const parsedDate = moment(dateStr, 'M/D/YYYY', true); // strict M/D/YYYY parsing
    return parsedDate.isValid()
      ? parsedDate.format('MM/DD/YYYY')
      : dateStr;
  };

  // Helper function to pad strings for alignment
  const padEnd = (str: string, length: number, char: string = ' '): string => {
    return str + char.repeat(Math.max(0, length - str.length));
  };

  return (
    <DashboardLayout title={"Orders Management"} initialBreadcrumbs={"Channel Partners"} levelBreadcrumbs={"Orders Management"} breadcrumbs={'Check Order Status'} activeMenu={'oms'}>
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
            Item From Orders Entered in The Past 24 Months
          </Typography>
          <Box>
            <Tooltip title="Download table data">
              <IconButton 
                color="error" 
                aria-label="Download"
                onClick={handleDownloadClick}
                aria-controls={downloadMenuOpen ? 'download-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={downloadMenuOpen ? 'true' : undefined}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Menu
              id="download-menu"
              anchorEl={downloadAnchorEl}
              open={downloadMenuOpen}
              onClose={handleDownloadClose}
              MenuListProps={{
                'aria-labelledby': 'download-button',
              }}
            >
              {downloadFormats.map((format) => (
                <MenuItem 
                  key={format.format} 
                  onClick={() => handleDownload(format.format)}
                  sx={{ minWidth: 150 }}
                >
                  <ListItemIcon>
                    <span style={{ fontSize: '16px' }}>{format.icon}</span>
                  </ListItemIcon>
                  <ListItemText>{format.label}</ListItemText>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Box>
        <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: "100%", overflowX: "auto", borderRadius: 0 }}>
          <Table size="small" aria-label="inventory list table">
            <TableHead sx={{ backgroundColor: "#c3183c"}}>
              <TableRow>
                <TableCell colSpan={8}  sx={{ color: 'white' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      Open Orders
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      Results: {Array.isArray(openItem) ? openItem.length : (openItem?.content?.length || 0)}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableHead sx={{ backgroundColor: "#f2f2f2" }}>
              <TableRow>
                <TableCol
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('orderNumber')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Sales Order
                    <Tooltip title="Click to sort">{getSortIcon('orderNumber')}</Tooltip>
                  </Box>
                </TableCol>
                <TableCol
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('purchaseOrder')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Purchase Order
                    <Tooltip title="Click to sort">{getSortIcon('purchaseOrder')}</Tooltip>
                  </Box>
                </TableCol>
                <TableCol
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('sortName')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Sort Name
                    <Tooltip title="Click to sort">{getSortIcon('sortName')}</Tooltip>
                  </Box>
                </TableCol>
                <TableCol
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('lineNumber')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Line
                    <Tooltip title="Click to sort">{getSortIcon('lineNumber')}</Tooltip>
                  </Box>
                </TableCol>
                <TableCol
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('itemNumber')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Item Number
                    <Tooltip title="Click to sort">{getSortIcon('itemNumber')}</Tooltip>
                  </Box>
                </TableCol>
                <TableCol
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('customerItem')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Customer Item
                    <Tooltip title="Click to sort">{getSortIcon('customerItem')}</Tooltip>
                  </Box>
                </TableCol>
                <TableCol
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('quantityOpen')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Open Qty
                    <Tooltip title="Click to sort">{getSortIcon('quantityOpen')}</Tooltip>
                  </Box>
                </TableCol>
                <TableCol
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('estimatedShipDate')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Estimated Ship Date
                    <Tooltip title="Click to sort">{getSortIcon('estimatedShipDate')}</Tooltip>
                  </Box>
                </TableCol>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCol colSpan={8}>
                    <Skeleton width="38%" />
                    <Skeleton width="100%" />
                    <Skeleton width="100%" />
                    <Skeleton width="61%" />
                  </TableCol>
                </TableRow>
              ) : hasFetched && displayData.length === 0 ? (
                <TableRow>
                  <TableCol colSpan={8} sx={{ textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No Data Found
                  </Typography>
                  </TableCol>
                </TableRow>
              ): ( 
                  displayData.map((order: any, idx: number) => (
                    <TableRow key={`${order.orderNumber}-${order.lineNumber}-${order.itemNumber}-${idx}`} hover>
                      <TableCol>{order.orderNumber}</TableCol>
                      <TableCol>{order.purchaseOrder}</TableCol>
                      <TableCol>{order.sortName}</TableCol>
                      <TableCol>{order.lineNumber}</TableCol>
                      <TableCol>{order.itemNumber}</TableCol>
                      <TableCol>{order.customerItem}</TableCol>
                      <TableCol>
                        {order.quantityOpenDisplay || order.quantityOpen}
                      </TableCol>
                      <TableCol>{formatEstimatedShipDate(order.estimatedShipDate)}</TableCol>
                    </TableRow>
                  ))
                )
              }
            </TableBody>
          </Table>
        </TableContainer>


        <Box sx={{ mt: 3, textAlign: 'left' }}>
          <Button variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important' }} onClick={() => navigate(-1)}>
            BACK
          </Button>
        </Box>
      </Box>
    </DashboardLayout>
  )
}

export default CheckOrderStatusItemDetails